/**
 * AfterParty.js
 *
 * What the after-party pages share (/fly/thank-you, /fly/souvenir, /fly/customs-declarations):
 * the page banner, the status card (sign in / not on the flight / errors), the signed-in guest's colour,
 * and the airport signs between the pages.
 */

import React, { useEffect, useState } from "react";
import { DoorOpen, Gift, Luggage, ScrollText, Stamp } from "lucide-react";
import { useClientAuth } from "../../../hooks/clientAuth";
import { getSouvenir } from "../helpers/flyApi";
import { AIRLINE_BLUE, PHOTOS_URL, Sign, SignGantry } from "./AirportSigns";

const INK = "#1B2A41";
const MUTED = "#5B6577";
const PAPER = "#FBF7EF";

export const bigButton = "w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full px-6 text-sm font-bold tracking-[0.14em] transition hover:opacity-90 disabled:opacity-60";

/**
 * The page's small label at the top, and anything under it (a heading, a line of text)
 * @param {Component} icon      -   A lucide icon
 * @param {String} label        -   e.g. 'SOUVENIRS'
 */
export const Banner = ({ icon: Icon, label, children }) => (
    <header className="flex flex-col items-center gap-2 text-center px-4 pt-8 sm:pt-12">
        <div className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.24em]" style={{ color: MUTED }}>
            <Icon size={16} aria-hidden="true" /> {label}
        </div>
        {children}
    </header>
);

/**
 * A card in the boarding pass style, for the sign-in / not on the flight / error screens
 */
export const StatusCard = ({ title, children }) => (
    <div className="w-full max-w-md mx-auto rounded-[22px] overflow-hidden text-center" style={{ background: PAPER, boxShadow: "0 10px 30px rgba(27,42,65,0.18)" }}>
        <div className="py-4 text-xs sm:text-sm font-bold tracking-[0.24em]" style={{ background: INK, color: PAPER }}>{title}</div>
        <div className="px-5 py-8 sm:px-8 flex flex-col items-center gap-4">{children}</div>
    </div>
);

/**
 * The signed-in guest's own colour, from the guest list (the airline's blue for everyone else)
 */
export const useGuestColour = () => {
    const { user, loading } = useClientAuth();
    const [colour, setColour] = useState(AIRLINE_BLUE);
    const uid = user?.uid;
    useEffect(() => {
        setColour(AIRLINE_BLUE);
        if (loading || !uid) return;
        let ignore = false;
        getSouvenir()
            .then((data) => { if (!ignore && data?.pass?.colour) setColour(data.pass.colour); })
            .catch(() => {});
        return () => { ignore = true; };
    }, [uid, loading]);
    return colour;
};

const PAGES = [
    { id: "note", icon: ScrollText, title: "Captain's note", subtitle: "A word from the flight deck", to: "/fly/thank-you", arrow: "left" },
    { id: "souvenirs", icon: Gift, title: "Souvenirs", subtitle: "Your boarding pass & passport", to: "/fly/souvenir" },
    { id: "declarations", icon: Stamp, title: "Customs Declarations", subtitle: "Everyone's two truths and a lie", to: "/fly/customs-declarations" },
    { id: "photos", icon: Luggage, title: "Baggage Claim", subtitle: "Party photos", href: PHOTOS_URL, arrow: "out" },
    { id: "exit", icon: DoorOpen, title: "Exit", subtitle: "isayyashtothat.com", to: "/" },
];

/**
 * Signs to every after-party page except the one you're on
 * @param {String} current      -   'note' | 'souvenirs' | 'declarations'
 * @param {String} colour       -   The signs' colour
 */
export const AfterPartySigns = ({ current, colour }) => (
    <SignGantry>
        {PAGES.filter((page) => page.id !== current).map(({ id, ...page }) => (
            <Sign key={id} {...page} colour={colour} />
        ))}
    </SignGantry>
);
